import React, { useEffect, useState } from "react";
// Core components
import ReactDOM from "react-dom";
//Css Here
import DashboardBox from "@components/DashboardBox";
import { RaffleDataBased, RaffleStatus } from "@type/Raffle";
import * as S from "./style";
import DashboardConstant from "@views/Pages/Dashboard/landing-image.constant";

//Css Here
import { getRaffle } from "@network/Raffle";
import { SCREEN } from "@type/index";
import RaffleSwiperRender from "./Components/RaffleSwipeRender";
import Img from "@assets/img";
import AlertPopup from "@components/AlertPopup";
import { routes } from "@const/routes";
import { useNavigate } from "react-router-dom";
import useThemeInitializer from "src/ThemeInitializer";
import { useFetchImages } from "src/Layouts/DashboardLayout/DashboardPreview";
import { RaffleKey, RaffleTitle } from "@const/profile";
import coins from "@assets/img/coins.png";
import defaultRaffleThumbnail from "@assets/img/raffle/defaultThumbnailRaffle.png";
import moment from "moment";
import HeroRaffle from "@components/DashboardBox/HeroRaffle";
import { getTicketCounts, TicketCountResponse } from "@network/Ticket";
import { updateFavorite } from "@network/Profile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { faHashtag, faHeart as faHeartSolid, faTimes } from "@fortawesome/free-solid-svg-icons";
import useAnalytics from "src/hooks/useAnalytics";
import { trackFunnel } from "@utils/analytics";

const Dashboard = ({ title }: { title: string }) => {
  // Handler to close the alert popup
  // const onFinish = () => setIsOpenCompletedAlert(false);
  const [raffleData, setRaffleData] = useState<RaffleDataBased>();
  const [nextTime, setNextTime] = useState();
  const [isOpenCompletedAlert, setIsOpenCompletedAlert] = useState(false);
  const [isOpenWelcomePopup, setIsOpenWelcomePopup] = useState(false);
  const navigate = useNavigate();
  const { fetchTheme, theme } = useThemeInitializer();
  const images = useFetchImages();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const { trackButtonClick } = useAnalytics();

  const nextRaffle = {
    title: "Next Game In:",
    raffle: nextTime,
    buttonText: "Join Now!",
  };

  const nextBlankRaffle = {
    title: "Next Game In:",
    blank: "Waiting",
  };

  const onFinish = () => {
    setIsOpenCompletedAlert(false);
  };

  const limit = 6;

  useEffect(() => {
    fetchAll();
    fetchTheme("dashboard");
  }, []);

  const fetchAll = async () => {
    const data = {
      limit: limit,
      offset: 0,
      type: "ALL",
      screen: SCREEN.DASHBOARD,
    };
    try {
      const response = await getRaffle(data);
      setNextTime(response.data.raffles.nextRaffle);
      setRaffleData(response.data);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  const handleClickRaffleCard = async (raffleId: string) => {
    try {
      const ticketCounts: TicketCountResponse = await getTicketCounts({
        screen: 'MY_TICKET_LIST'
      });

      // Track game selected
      trackFunnel.gameSelected("raffle", raffleId, "raffle");
      trackButtonClick("view_raffle_details", { raffle_id: raffleId });

      // Show welcome popup if user has 0 total tickets
      if (ticketCounts.totalTickets === 0) {
        setIsOpenWelcomePopup(true);
      } else {
        navigate(`${routes.raffle}/${raffleId}`);
      }
    } catch (error) {
      console.error("Error checking ticket counts:", error);
    }
  }

  const handleWelcomePopupClose = () => {
    setIsOpenWelcomePopup(false);
    localStorage.setItem("hasSeenWelcomePopup", "true");
  };

  const handleGetTickets = () => {
    setIsOpenWelcomePopup(false);
    localStorage.setItem("hasSeenWelcomePopup", "true");
    navigate(routes.ticketGenerator);
  };

  const firstRaffleItem = (() => {
    if (!raffleData) return null;
    for (const item of RaffleKey) {
      const data = (raffleData?.raffles as any)?.[item]?.raffles || [];
      const heroRaffle = data.find((raffle: any) => raffle.isHeroRaffle || raffle.is_hero_raffle);
      if (heroRaffle) {
        return heroRaffle;
      }
      if (data.length > 0) {
        return data[0]; // Return the first raffle if no hero raffle is found
      }
    }
    return null;
  })();

  // Separate useEffect for timer
  useEffect(() => {
    if (!firstRaffleItem?.expiredDate) return;

    const updateTimer = () => {
      const now = new Date();
      const endTime = new Date(firstRaffleItem.expiredDate);
      const timeRemaining = endTime.getTime() - now.getTime();
      setTimeLeft(Math.max(timeRemaining, 0));
    };

    // Set initial time
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [firstRaffleItem?.expiredDate]);

  const getDynamicRaffleImage = (raffle: any) => {
    if (!raffle) return "";

    // Only apply this logic for Daily and Weekly raffles
    if (raffle.type?.toLowerCase() !== 'daily' && raffle.type?.toLowerCase() !== 'weekly') {
      return raffle.thumbnailImg || defaultRaffleThumbnail;
    }

    const isWeekly = raffle.type?.toLowerCase() === 'weekly';
    const referenceDate = isWeekly ? moment(raffle.expiredDate).local() : moment(raffle.startTime).local();
    
    const dayName = referenceDate.format('dddd').toUpperCase();
    const dayDigit = referenceDate.format('DD');
    const monthName = referenceDate.format('MMMM').toUpperCase();
    
    const statusStr = (raffle.status || "").toString().toUpperCase();
    const isLive = statusStr === RaffleStatus.ELIGIBLE || statusStr === RaffleStatus.HAPPENING || statusStr === 'ACTIVE';
    const isDone = statusStr === RaffleStatus.DONE;
    
    let state = "COMING SOON";
    if (isLive) state = "LIVE";
    if (isDone) state = "END";

    // Standardized format: "/raffle/[DAY] [DD] [MONTH] [STATE].png"
    // Example: "/raffle/FRIDAY 10 APRIL LIVE.png"
    return `/raffle/${dayName} ${dayDigit} ${monthName} ${state}.png`;
  };

  return (
    <>
      {/* Modern Hero Section */}
      <section className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] overflow-hidden md:mt-12">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-56 h-56 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>

        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 py-8 lg:py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">

            {/* Left Content */}
            <div className="flex-1 text-white space-y-4">
              {/* Badge */}


              {/* Main Heading */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight">
                Join the Fun & Win Prizes Every Single Day!{" "}
              </h1>

              {/* Subheading */}
              <p className="text-base md:text-lg text-white/90 max-w-2xl">
                Play exciting games, answer questions, and win amazing rewards daily. No purchase required!
              </p>

              {/* CTA Buttons */}


              {/* Stats */}


              {/* Terms */}
              <p className="text-xs text-white/60 pt-1">
                *Terms and Conditions apply
              </p>
            </div>

            {/* Right Content - Logo/Mascot */}
            <div className="flex-shrink-0 lg:w-64 xl:w-72 hidden lg:block">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl"></div>

                {/* Logo Container */}
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <img
                    src={images?.logoImage1 ?? theme?.logoImage1 ?? "https://wnnr.fsn1.your-objectstorage.com/b77d8728-bf73-4bf8-8a8c-9aa229ef7f5b.jpg"}
                    alt="Dabur Game Center"
                    className="w-full h-auto object-contain drop-shadow-2xl"
                  />

                  {/* Winners Club Badge */}
                  <div className="mt-4 bg-white text-[#4B975B] px-4 py-2 rounded-xl text-center">
                    <span className="text-base font-bold uppercase tracking-wider">
                      🏆 Winners Club
                    </span>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-3 -right-3 bg-yellow-400 text-yellow-900 px-3 py-1.5 rounded-full font-bold text-xs shadow-lg animate-bounce">
                  New!
                </div>
                <div className="absolute -bottom-3 -left-3 bg-white text-[#4B975B] px-3 py-1.5 rounded-full font-bold text-xs shadow-lg">
                  Free Gifts
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 md:h-12" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#EAF0D5" />
          </svg>
        </div>
      </section>

      <div className=" mb-10 px-4">
        {firstRaffleItem && (
          <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
            {/* Featured Live Raffle Card */}
            <div className="relative bg-gradient-to-br from-white to-[#f8faf9] rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border-2 border-[#4B975B]/20">

              {/* Live Badge - Top Left */}
              {firstRaffleItem?.status === RaffleStatus.ELIGIBLE || firstRaffleItem?.status === RaffleStatus.HAPPENING ? (
                <div className="absolute -top-2 left-3 sm:top-3 sm:left-4 md:left-6 bg-red-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 animate-pulse z-10">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                  <span className="hidden xs:inline">LIVE NOW</span>
                  <span className="xs:hidden">LIVE</span>
                </div>
              ) : firstRaffleItem?.status === RaffleStatus.DONE ? (
                <div className="absolute -top-2 left-3 sm:top-3 sm:left-4 md:left-6 bg-[#4b975b] text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 z-10">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                  COMPLETED
                </div>
              ) : (
                <div className="absolute -top-2 left-3 sm:top-3 sm:left-4 md:left-6 bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 z-10">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                  UPCOMING
                </div>
              )}

              {/* 100% Free Badge - Top Right */}
              <div className="absolute -top-2 right-3 sm:top-3 sm:right-4 md:top-4 md:right-4 z-10">
                <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 rounded-full shadow-lg transform rotate-2 sm:rotate-3">
                  <span className="text-[10px] sm:text-xs md:text-sm font-black uppercase">100% Free</span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 p-4 sm:p-6 md:p-8 lg:p-10">


                {/* Left Section - Image */}
                <div className="flex items-center justify-center px-0 sm:px-2">
                  <div className="relative w-full max-w-[280px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[432px]" style={{ aspectRatio: '1/1' }}>
                    {/* Glow effect behind image */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#4B975B]/20 to-[#ACC992]/20 rounded-2xl sm:rounded-3xl blur-xl sm:blur-2xl"></div>

                    {/* Image Container */}
                    <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden border-2 sm:border-3 md:border-4 border-white shadow-xl sm:shadow-2xl bg-white w-full h-full">
                      <img
                        src={getDynamicRaffleImage(firstRaffleItem)}
                        alt={firstRaffleItem.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          if (target.src.includes('COMING%20SOON')) {
                            target.src = target.src.replace('COMING%20SOON', 'LIVE');
                          } else {
                            target.src = firstRaffleItem.thumbnailImg || defaultRaffleThumbnail;
                            target.onerror = null;
                          }
                        }}
                      />
                    </div>

                    {/* Floating Prize Badge */}
                    <div className="absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 sm:px-6 sm:py-3 rounded-full shadow-lg sm:shadow-xl border-2 border-[#4B975B]">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-xl md:text-2xl">🎁</span>
                        <span className="font-bold text-[#4B975B] text-xs sm:text-sm md:text-base">Big Prizes</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Section - Content */}
                <div className="flex flex-col justify-center space-y-4 sm:space-y-5 md:space-y-6 mt-4 lg:mt-0">

                  {/* Title */}
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1f2c2b] leading-tight mb-2 sm:mb-3">
                      {firstRaffleItem.title}
                    </h2>

                    {/* Date Range */}
                    {(firstRaffleItem.startTime || firstRaffleItem.expiredDate) && (
                      <div className="flex items-center gap-1.5 sm:gap-2 text-[#6a7b76]">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs sm:text-sm md:text-base font-bold text-[#4B975B]">
                          {firstRaffleItem.type?.toLowerCase() === 'weekly' ? (
                            <div className="flex flex-col">
                              {/* <span>Result Date: {moment(firstRaffleItem.expiredDate).format('DD MMM, YYYY')}</span> */}
                              <span className="text-[10px] sm:text-xs text-gray-500 font-normal">
                                ({moment(firstRaffleItem.startTime).format('DD MMM')} - {moment(firstRaffleItem.expiredDate).format('DD MMM, YYYY')})
                              </span>
                            </div>
                          ) : (
                            moment(firstRaffleItem.startTime).format('DD MMM, YYYY')
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {firstRaffleItem.description && (
                    <p className="text-sm sm:text-base md:text-lg text-[#6a7b76] leading-relaxed line-clamp-3 sm:line-clamp-none">
                      {firstRaffleItem.description}
                    </p>
                  )}

                  {/* Countdown Timer */}
                  <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#ACC992]/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-[#4B975B]/20">
                    {timeLeft > 0 ? (
                      (() => {
                        const now = moment();
                        const startTime = moment(firstRaffleItem.startTime);
                        const endTime = moment(firstRaffleItem.endTime);

                        const isStarted = now.isSameOrAfter(startTime);
                        const timeRemaining = isStarted
                          ? endTime.diff(now)
                          : startTime.diff(now);

                        if (timeRemaining <= 0) {
                          return (
                            <div className="text-center py-3 sm:py-4">
                              <span className="text-xl sm:text-2xl">🎉</span>
                              <p className="text-base sm:text-lg font-bold text-[#1f2c2b] mt-2">Raffle Ended</p>
                            </div>
                          );
                        }

                        const duration = moment.duration(timeRemaining);
                        const days = Math.floor(duration.asDays());
                        const hours = duration.hours();
                        const minutes = duration.minutes();
                        const seconds = duration.seconds();

                        const timeUnits = [];
                        if (days > 0) {
                          timeUnits.push({ value: days, label: "Days" });
                          timeUnits.push({ value: hours, label: "Hours" });
                          timeUnits.push({ value: minutes, label: "Mins" });
                        } else if (hours > 0) {
                          timeUnits.push({ value: hours, label: "Hours" });
                          timeUnits.push({ value: minutes, label: "Mins" });
                          timeUnits.push({ value: seconds, label: "Secs" });
                        } else {
                          timeUnits.push({ value: minutes, label: "Mins" });
                          timeUnits.push({ value: seconds, label: "Secs" });
                        }

                        return (
                          <div className="space-y-2 sm:space-y-3">
                            <p className="text-xs sm:text-sm font-semibold text-[#1f2c2b] text-center">
                              {isStarted ? "⏰ Entries Close in:" : "⏰ Entries Open in:"}
                            </p>
                            <div className="flex items-center justify-center gap-2 sm:gap-3">
                              {timeUnits.map((unit, index) => (
                                <React.Fragment key={unit.label}>
                                  <div className="bg-white rounded-lg sm:rounded-xl px-2 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3 shadow-md min-w-[50px] sm:min-w-[60px] md:min-w-[70px] text-center border-2 border-[#4B975B]/20">
                                    <div className="text-xl sm:text-2xl md:text-3xl font-black text-[#4B975B]">
                                      {unit.value.toString().padStart(2, "0")}
                                    </div>
                                    <div className="text-[9px] sm:text-xs font-semibold text-[#6a7b76] uppercase mt-0.5 sm:mt-1">
                                      {unit.label}
                                    </div>
                                  </div>
                                  {index < timeUnits.length - 1 && (
                                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#4B975B]">:</div>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-center py-3 sm:py-4">
                        <span className="text-xl sm:text-2xl">🎉</span>
                        <p className="text-base sm:text-lg font-bold text-[#1f2c2b] mt-2">Raffle Started!</p>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleClickRaffleCard(firstRaffleItem.id)}
                    disabled={firstRaffleItem.isPlayed || firstRaffleItem.status === RaffleStatus.WAITING_PEOPLE || firstRaffleItem.status === RaffleStatus.CANCEL || firstRaffleItem.status === RaffleStatus.DONE}
                    className="group relative bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white px-6 py-3.5 sm:px-7 sm:py-4 md:px-8 md:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg md:text-xl shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                    <div className="relative flex items-center justify-center gap-2 sm:gap-3">
                      {firstRaffleItem.isPlayed ? (
                        <span>Already Played</span>
                      ) : firstRaffleItem.status === RaffleStatus.HAPPENING || firstRaffleItem.status === RaffleStatus.ELIGIBLE ? (
                        <>
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                          </svg>
                          <span>Play Now & Win!</span>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </>
                      ) : firstRaffleItem.status === RaffleStatus.WAITING_PEOPLE ? (
                        <>
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                          </svg>
                          <span>Coming Soon</span>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                          </svg>
                          <span>{firstRaffleItem.status}</span>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </>
                      )}
                    </div>
                  </button>

                  {/* Quick Stats */}
                  <div className="flex flex-wrap gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-[#4B975B]/20">

                    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#6a7b76]">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#4B975B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">No Purchase Required</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#6a7b76]">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#4B975B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Verified Winners</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="px-2 sm:px-3 md:px-5">
          <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8">
            {/* All Raffles Grid */}
            {raffleData && (
              <>
                {RaffleKey.map((raffleType: string) => {
                  const raffles = (raffleData?.raffles as any)?.[raffleType]?.raffles || [];

                  if (raffles.length === 0) return null;

                  return (
                    <div key={raffleType} className="mb-8 sm:mb-10 md:mb-12">
                      {/* Section Title */}
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1f2c2b] mb-4 sm:mb-5 md:mb-6">
                        {RaffleTitle[raffleType]}
                      </h2>

                      {/* Grid of Raffles - Responsive: 1 col mobile, 3 col tablet+ */}
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                        {raffles.map((raffle: any) => (
                          <div
                            key={raffle.id}
                            // onClick={() => navigate(`${routes.raffle}/${raffle.id}`)}
                            className="group bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-[#4B975B]/30"
                          >
                            {/* Image Container - Responsive height */}
                            <div className="relative bg-gray-50 overflow-hidden">
                              <img
                                src={getDynamicRaffleImage(raffle)}
                                alt={raffle.title}
                                className="w-full aspect-1"
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  if (target.src.includes('COMING%20SOON')) {
                                    target.src = target.src.replace('COMING%20SOON', 'LIVE');
                                  } else {
                                    target.src = raffle.thumbnailImg || defaultRaffleThumbnail;
                                    target.onerror = null;
                                  }
                                }}
                              />

                              {/* Status Badge */}
                              {raffle.status === 'ACTIVE' && (
                                <div className="absolute top-3 left-3 sm:top-4 sm:left-4 bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5">
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></div>
                                  Active
                                </div>
                              )}

                              {/* Favorite Button */}
                              {/* <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await updateFavorite(raffle.raffleId);
                                    fetchAll();
                                    // Refresh data or update state
                                  } catch (err) {
                                    console.error(err);
                                  }
                                }}
                                className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all duration-200 shadow-lg active:scale-95"
                              >
                                <FontAwesomeIcon
                                  icon={raffle.favorite ? faHeartSolid : faHeartRegular}
                                  className={`text-base sm:text-lg ${raffle.favorite ? 'text-red-500' : 'text-gray-400'}`}
                                />
                              </button> */}

                              {/* Hover Overlay - Hidden on mobile, shown on hover for desktop */}
                              {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 sm:pb-6">
                                <div className="flex items-center gap-2 text-white font-semibold text-sm sm:text-base">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                  </svg>
                                  <span>Play Now</span>
                                </div>
                              </div> */}
                            </div>

                            {/* Content Below Image */}
                            <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
                              {/* Title */}
                              <h3 className="text-lg sm:text-xl font-bold text-[#1f2c2b] line-clamp-2 group-hover:text-[#4B975B] transition-colors">
                                {raffle.title}
                              </h3>

                              {/* Date Range */}
                              {(raffle.startTime || raffle.expiredDate) && (
                                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#6a7b76]">
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate font-bold text-[#4B975B]">
                                    {raffle.type?.toLowerCase() === 'weekly' ? (
                                      <div className="flex flex-col">
                                        {/* <span>Result Date: {moment(raffle.expiredDate).format('DD MMM, YYYY')}</span> */}
                                        <span>
                                          {moment(raffle.startTime).format('DD MMM')} - {moment(raffle.expiredDate).format('DD MMM, YYYY')}
                                        </span>
                                      </div>
                                    ) : (
                                      moment(raffle.startTime).format('MMM DD, YYYY')
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Stats */}
                              <div className="flex items-center gap-3 sm:gap-4 pt-2 border-t border-gray-100">
                                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#6a7b76]">
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#4B975B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                  </svg>
                                  <span className="font-medium">{raffle.numberOfParticipants || 0} Entries</span>
                                </div>
                                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[#6a7b76]">
                                  <FontAwesomeIcon icon={faHashtag} className="w-4 h-4 sm:w-5 sm:h-5 text-[#4B975B] flex-shrink-0" />
                                  <span className="font-medium">{raffle.numberOfTurns || 0} Turn</span>
                                </div>
                              </div>

                              {/* Play Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`${routes.raffle}/${raffle.id}`);
                                }}
                                disabled={raffle?.status === RaffleStatus.WAITING_PEOPLE || raffle?.status === RaffleStatus.CANCEL || raffle?.status === RaffleStatus.DONE}
                                className="w-full bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-98 text-sm sm:text-base"
                              >
                                {raffle.isPlayed ? 'Already Played' : raffle?.status === RaffleStatus.HAPPENING || raffle?.status === RaffleStatus.ELIGIBLE ? (
                                  <>
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                    </svg>
                                    Enter Raffle
                                  </>
                                ) : raffle?.status === RaffleStatus.WAITING_PEOPLE ? (
                                  <>
                                    <svg className="w-5 h-5 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                    </svg>
                                    <span>Coming Soon</span>
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-5 h-5 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                                    </svg>
                                    <span>{raffle?.status}</span>
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* No Raffles Message */}
                {RaffleKey.every((item: string) => {
                  const data = (raffleData?.raffles as any)?.[item]?.raffles || [];
                  return data.length === 0;
                }) && (
                    <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                      <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🎲</div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#1f2c2b] mb-2 text-center px-4">No Raffles Available</h3>
                      <p className="text-sm sm:text-base text-[#6a7b76] text-center px-4">Check back soon for exciting new raffles!</p>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        {/* <div className="wheel-game-container">
          <h1>{theme.name ?? "Primary"} Insta-Win</h1>
          <img src={theme.logoImage2 ?? Img.wheelGame} alt="Wheel Game" className="wheel-game-image" />
        </div> */}
      </div>
      <AlertPopup
        isOpen={isOpenCompletedAlert}
        header="Congratulations"
        title={
          <>
            Thank you for your participation!
            <br />
            You have 1 Free Ticket!
          </>
        }
        icon={Img.StarIcon}
        leftButton={{
          title: "View Your Tickets",
          onClick: () => navigate(routes.ticket),
        }}
        onDismiss={onFinish}
      />
      {isOpenWelcomePopup && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
          onClick={handleWelcomePopupClose}
        >
          <div
            className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl w-full overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleWelcomePopupClose}
              className="absolute top-4 right-4 z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
            </button>

            {/* Header Section with Gradient */}
            <div className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] px-6 py-8 sm:px-8 sm:py-10 text-center">
              {/* Decorative circles */}
              <div className="absolute top-0 left-0 w-20 h-20 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 sm:w-40 sm:h-40 bg-white/10 rounded-full translate-x-1/3 translate-y-1/3"></div>

              {/* Icon */}
              <div className="hidden relative mb-4 md:flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-50 animate-pulse"></div>
                  <img
                    src={Img.StarIcon}
                    alt="Star"
                    className="relative w-16 h-16 sm:w-24 sm:h-24 drop-shadow-2xl animate-bounce"
                  />
                </div>
              </div>

              {/* Title */}
              <h2 className="relative text-lg sm:text-3xl md:text-4xl font-extrabold text-white mb-2">
                Welcome to Winners Club!
              </h2>
            </div>

            {/* Content Section */}
            <div className="p-3 sm:p-8">
              {/* Main Message */}
              <div className="text-center mb-6 sm:mb-8">
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  Get started by earning your first tickets!
                </p>
                <p className="text-sm sm:text-base text-gray-600 mt-2 sm:mt-3 leading-relaxed">
                  Answer questions, complete your profile, or invite friends to earn tickets and win amazing prizes.
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
                <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#ACC992]/10 rounded-xl sm:rounded-2xl p-2 md:p-4 text-center border border-[#4B975B]/20">
                  <div className="text-2xl sm:text-3xl mb-2">❓</div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-700">Answer Questions</p>
                </div>
                <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#ACC992]/10 rounded-xl sm:rounded-2xl p-2 md:p-4 text-center border border-[#4B975B]/20">
                  <div className="text-2xl sm:text-3xl mb-2">👤</div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-700">Complete Profile</p>
                </div>
                <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#ACC992]/10 rounded-xl sm:rounded-2xl p-2 md:p-4 text-center border border-[#4B975B]/20">
                  <div className="text-2xl sm:text-3xl mb-2">👥</div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-700">Invite Friends</p>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleGetTickets}
                className="group relative w-full bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                <div className="relative flex items-center justify-center gap-2 sm:gap-3">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                  </svg>
                  <span>Get your tickets now!</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Dashboard;
